import { Box, Grid, Typography } from "@mui/material";
import { ConnectedDevice, InstallationStatus, Status } from "../../../helper/cloudcore/response/models/body";
import { CloudCoreFactory } from "../../../helper/cloudcore/factory";
import { InstallGadgetOnDeviceRequest } from "../../../helper/cloudcore/request/models/events";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../common/state/hooks";
import { RootState } from "../../../common/state";
import styles from "../css/ConnectedDeviceCard.module.css";
import { FlattenedGadget } from "../../../models/GadgetModel";
import { GadgetDaoFactory } from "../../Home/components/gadgets/GadgetRepository";
import { update } from "../../../common/state/Slices/InstalledGadgetsSlice";
import DeviceImage from "../../../images/device.svg";
import { DeviceConnectionStatusButton } from "./DeviceConnectionStatusButton";
import { InstallationStatusLabel } from "./InstallationStatusLabel";
import { GadgetInstallButton } from "./GadgetInstallButton";
import { initialize } from "../../../common/state/Slices/InstallOnAllDeviceSlice";
import { notify } from "../../../common/state/Slices/NotificationSlice";
import { getGPKUrl } from "../../../helper/utils";

interface ConnectedDeviceCardProps {
    device: ConnectedDevice,
    dataTestId: string,
    displayGadget: FlattenedGadget,
    isOnline: boolean;
}

export const ConnectedDeviceCard = ({device, dataTestId, displayGadget, isOnline}: ConnectedDeviceCardProps) => {
    const cloudCoreClient = CloudCoreFactory.getInstance();
    const deviceData = useAppSelector((state: RootState) => state.installedGadgetSlice.deviceData);
    const [isInstallationInProgress, setIsInstallationInProgress] = useState<boolean>(false);
    const [installationStatus, setInstallationStatus] = useState<Status>(InstallationStatus.ERROR);
    const [installationProgress, setInstallationProgress] = useState<number>(0);
    const [didInstallationFinish, setDidInstallationFinish] = useState<boolean>(false);
    const dispatch = useAppDispatch();

    const handleInstall = () => {
        setIsInstallationInProgress(true);
        setDidInstallationFinish(false);
        setInstallationStatus(InstallationStatus.INSTALLING);
        const gadgeturlRequest = getGPKUrl(displayGadget);

        GadgetDaoFactory.getGadgetRepository()
        .getPreSignedUrl(displayGadget.id, gadgeturlRequest)
        .then((result) => {
            const request = new InstallGadgetOnDeviceRequest(device.user_id, result.url, [device.connection_id], displayGadget.size, displayGadget.id);
            cloudCoreClient.sendEvent(request);
            
            dispatch(
                update(
                    { 
                        sourceDeviceId: device.id,
                        gadgetId: displayGadget.id,
                        status: InstallationStatus.INSTALLING,
                        progress: 0
                    }
                )
            );

            dispatch(
                initialize({
                    gadgetId: displayGadget.id,
                    remainingDevices: null
                })
            );
        })
        .catch(() => {
            const errorMessage = "You are not authorized to install this gadget";
            dispatch(
                notify({
                    message: errorMessage,
                    severity: "error"
                })
            );
            dispatch(
                update(
                    { 
                        sourceDeviceId: device.id,
                        gadgetId: displayGadget.id,
                        status: InstallationStatus.ERROR,
                        progress: 0
                    }
                )
            );
            console.error(errorMessage);
        });
    }

    useEffect(() => {
        const currentDeviceData = deviceData[device.id];
        if(currentDeviceData && displayGadget.id in currentDeviceData) {
            const installationStatus = currentDeviceData[displayGadget.id];
            setInstallationStatus(installationStatus.status);
            if((installationStatus.status === InstallationStatus.SUCCESS || installationStatus.status === InstallationStatus.ERROR)) {
                setInstallationProgress(0);
                setIsInstallationInProgress(false);
                setDidInstallationFinish(true);
            } else {
                setInstallationProgress(installationStatus.progress);
                setIsInstallationInProgress(true);
                setDidInstallationFinish(false);
            }
        }
        // eslint-disable-next-line
    }, [deviceData]);

    return (
        <Box data-testid={dataTestId} key={device.connection_id}>
            <Box className={styles.cardContentContainer}>
                <Grid container className={styles.gridContainer}>
                    <Grid item xs={1}>
                        <img src={DeviceImage} alt="device icon" data-testid={"device-icon"}/>
                    </Grid>
                    <Grid item xs={7}>
                        <Box className={styles.deviceDetailsContainer}>
                            <Typography variant="h2" data-testid={"device-name"} className={styles.deviceNameText}>{device.name}</Typography>
                            <Typography variant="body2" data-testid={"device-serial-number"} className={styles.serialNumberText}>{device.serial_number}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={4} className={styles.grid}>
                        <DeviceConnectionStatusButton isOnline={isOnline} />
                        {
                            didInstallationFinish && isOnline
                            ? 
                            <InstallationStatusLabel
                                handleInstall={handleInstall} 
                                installationStatus = {installationStatus}
                            />
                            : 
                            <GadgetInstallButton
                                isInstallationInProgress={isInstallationInProgress} 
                                handleInstall={handleInstall} 
                                isOnline={isOnline}
                                installationProgress={installationProgress}
                            />
                        }
                    </Grid>
                </Grid>
            </Box>
        </Box>
    )
}